import { useState } from 'react';
import { useAction, useApi, useIntentKey } from '../../api/hooks.js';
import { createThreatEvent, listThreatEvents, retractThreatEvent, type ThreatEventCreated } from '../api.js';
import { THREAT_KINDS, threatEventBody, type Problem, type ThreatEventBody, type ThreatEventForm } from '../forms.js';
import { fmtWhen, kindLabel, shortId, statusLabel } from '../labels.js';
import { Card, Empty, Field, Head, Loading, problemFor, Problems, ReasonStrip, show, Tag, Trouble } from '../ui.js';

/**
 * Threat events: something happened in the world — a leak, a wave of deepfakes
 * on a site — and every person with a live match there should see it in their
 * protection score. Creating one applies immediately to everyone it matches,
 * so the form ends in a review step that says so, and the request carries one
 * Idempotency-Key per intent so a repeated click cannot create it twice.
 * Retracting reverses the effect exactly.
 */

const BLANK: ThreatEventForm = {
  kind: 'leak',
  title: '',
  body: '',
  severity: '3',
  domains: '',
  isGlobal: false,
  penalty: '',
  expiresAt: '',
  decayDays: '14',
};

export function ThreatsScreen() {
  const events = useApi(listThreatEvents, []);
  const [form, setForm] = useState<ThreatEventForm>(BLANK);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [staged, setStaged] = useState<ThreatEventBody | null>(null);
  const [created, setCreated] = useState<ThreatEventCreated | null>(null);
  const intent = useIntentKey();

  const create = useAction(
    (body: ThreatEventBody) => createThreatEvent(body, intent.key),
    (result) => {
      intent.next();
      setCreated(result);
      setStaged(null);
      setForm(BLANK);
      events.reload();
    },
  );
  const retract = useAction(
    (eventId: string, reason: string) => retractThreatEvent(eventId, reason),
    () => events.reload(),
  );

  const set = <K extends keyof ThreatEventForm>(key: K, value: ThreatEventForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const review = () => {
    const result = threatEventBody(form);
    if (result.ok) {
      setProblems([]);
      setStaged(result.body);
    } else {
      setProblems(result.problems);
      setStaged(null);
    }
  };

  return (
    <>
      <Head
        title="Threat events"
        lede="Record something that happened in the world — a leak, a wave of deepfakes on a site — and everyone with a live match there has their protection score lowered for a while. The effect fades over time, and retracting an event reverses it exactly. People are not messaged; only their score moves."
      />

      <Card title="New threat event">
        {created !== null && (
          <div className="cr-notice ok" role="status">
            <strong>
              Created. It matched {String(created.matched_count)} {created.matched_count === 1 ? 'person' : 'people'}.
            </strong>
            <span>Their scores have already been recalculated. Event {shortId(created.event_id)}.</span>
          </div>
        )}
        {staged === null ? (
          <form
            className="cr-form"
            onSubmit={(event) => {
              event.preventDefault();
              review();
            }}
          >
            <div className="cr-form-row">
              <Field label="Kind" problem={problemFor(problems, 'kind')}>
                <select value={form.kind} onChange={(event) => set('kind', event.target.value)}>
                  {THREAT_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {kindLabel(kind)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Severity" hint="1 (minor) to 5 (severe)" problem={problemFor(problems, 'severity')}>
                <input
                  type="number"
                  min={1}
                  max={5}
                  step={1}
                  value={form.severity}
                  onChange={(event) => set('severity', event.target.value)}
                />
              </Field>
              <Field label="Score penalty" hint="points, e.g. 5.00" problem={problemFor(problems, 'penalty')}>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="5.00"
                  value={form.penalty}
                  onChange={(event) => set('penalty', event.target.value)}
                />
              </Field>
            </div>
            <Field label="Title" problem={problemFor(problems, 'title')}>
              <input
                type="text"
                placeholder="e.g. Leak of member photos on example.com"
                value={form.title}
                onChange={(event) => set('title', event.target.value)}
              />
            </Field>
            <Field label="Notes" hint="internal, optional" problem={problemFor(problems, 'body')}>
              <textarea value={form.body} rows={2} onChange={(event) => set('body', event.target.value)} />
            </Field>
            <Field
              label="Websites affected"
              hint="one per line, or separated by commas"
              problem={problemFor(problems, 'domains')}
            >
              <textarea
                value={form.domains}
                rows={2}
                disabled={form.isGlobal}
                placeholder={form.isGlobal ? 'Applies to every website' : 'example.com, another-site.net'}
                onChange={(event) => set('domains', event.target.value)}
              />
            </Field>
            <label className="cr-check">
              <input type="checkbox" checked={form.isGlobal} onChange={(event) => set('isGlobal', event.target.checked)} />
              Applies to everyone with a live match, on any website
            </label>
            <div className="cr-form-row">
              <Field label="Expires" hint="your local time" problem={problemFor(problems, 'expiresAt')}>
                <input type="datetime-local" value={form.expiresAt} onChange={(event) => set('expiresAt', event.target.value)} />
              </Field>
              <Field label="Fades over" hint="days" problem={problemFor(problems, 'decayDays')}>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={form.decayDays}
                  onChange={(event) => set('decayDays', event.target.value)}
                />
              </Field>
            </div>
            <Problems problems={problems} />
            <div className="cr-actions">
              <button type="submit" className="cr-btn primary">
                Review before creating
              </button>
              <span className="cr-fine">Nothing is applied until you confirm on the next step.</span>
            </div>
          </form>
        ) : (
          <div className="cr-confirm" role="group" aria-label="Confirm the new threat event">
            <strong>This takes effect the moment it is created.</strong>
            <span className="cr-muted">
              Everyone with a live match {staged.is_global ? 'on any website' : `on ${staged.domains.join(', ')}`} has their
              protection score lowered by {staged.penalty} points, fading over {String(staged.decay_days)} days, until{' '}
              {fmtWhen(staged.expires_at)}. Retracting later reverses it exactly.
            </span>
            <dl className="cr-kv">
              <dt>Kind</dt>
              <dd>{kindLabel(staged.kind)}</dd>
              <dt>Title</dt>
              <dd>{staged.title}</dd>
              <dt>Severity</dt>
              <dd>{String(staged.severity)} of 5</dd>
            </dl>
            <div className="cr-actions">
              <button type="button" className="cr-btn danger" disabled={create.pending} onClick={() => create.run(staged)}>
                {create.pending ? 'Creating…' : 'Create the event now'}
              </button>
              <button type="button" className="cr-btn ghost" disabled={create.pending} onClick={() => setStaged(null)}>
                Back to editing
              </button>
            </div>
            {create.error !== null && <Trouble error={create.error} />}
          </div>
        )}
      </Card>

      <Card title="Events" aside={<span className="cr-fine">Newest first</span>}>
        {events.error !== null ? (
          <Trouble error={events.error} />
        ) : events.data === null ? (
          <Loading />
        ) : events.data.events.length === 0 ? (
          <Empty>No threat events yet.</Empty>
        ) : (
          <div className="cr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Title</th>
                  <th className="num">Severity</th>
                  <th>Applies to</th>
                  <th className="num">Penalty</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th className="num">Fades over</th>
                  <th>Created by</th>
                  <th>Retract</th>
                </tr>
              </thead>
              <tbody>
                {events.data.events.map((event) => (
                  <tr key={event.event_id}>
                    <td>
                      <Tag tone="brand">{kindLabel(event.kind)}</Tag>
                    </td>
                    <td>
                      <strong>{event.title}</strong>
                      {event.body !== '' && event.body !== undefined && <div className="cr-fine">{event.body}</div>}
                    </td>
                    <td className="num">{show(event.severity)}</td>
                    <td>{event.is_global ? <Tag tone="warn">Every website</Tag> : (event.domains ?? []).join(', ')}</td>
                    <td className="num">{show(event.penalty)}</td>
                    <td>
                      <Tag tone={event.status === 'active' ? 'ok' : event.status === 'retracted' ? 'bad' : undefined}>
                        {statusLabel(event.status)}
                      </Tag>
                    </td>
                    <td>{fmtWhen(event.expires_at)}</td>
                    <td className="num">{typeof event.decay_days === 'number' ? `${String(event.decay_days)} days` : show(event.decay_days)}</td>
                    <td>{show(event.created_by)}</td>
                    <td style={{ minWidth: 300 }}>
                      {event.status === 'active' ? (
                        <ReasonStrip
                          verb="Retract"
                          tone="danger"
                          pending={retract.pending}
                          placeholder="Why — every affected score is restored"
                          onRun={(reason) => retract.run(event.event_id, reason)}
                        />
                      ) : (
                        <span className="cr-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {retract.error !== null && <Trouble error={retract.error} />}
        {retract.result !== null && (
          <div className="cr-notice ok" role="status">
            <strong>Retracted.</strong>
            <span>
              {String(retract.result.matched_count)} {retract.result.matched_count === 1 ? 'score' : 'scores'} restored.
            </span>
          </div>
        )}
      </Card>
    </>
  );
}
